import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Chart } from 'react-google-charts';
import ButtonSwitch from 'shared/components/button-switch/ButtonSwitch';
import shortid from 'shortid';
import moment from 'moment';
import { updateClientTrack } from 'features/clientProfile/components/progressTab/ducks/ProgressActions';

const ProgressGraphsItem = ({
  title,
  data,
  xAxis,
  yAxis,
  intervalSelected,
  setIntervalSelected,
  showSwitchBtn,
  isEnabled,
  updateClientTrack,
  trackKey,
  clientId,
  ...props
}) => {
  const intervals = [
    { key: 'oneWeek', value: '1 wk' },
    { key: 'oneMonth', value: '1 m' },
    { key: 'threeMonths', value: '3 m' },
    { key: 'sixMonths', value: '6 m' },
    { key: 'oneYear', value: '1 y' }
  ];

  const intervalsFormats = {
    oneWeek: 'ddd, MMM D',
    oneMonth: 'WW',
    threeMonths: 'WW',
    sixMonths: 'MMM',
    oneYear: 'MMM'
  };

  const intervalsStep = {
    oneWeek: 'day',
    oneMonth: 'week',
    threeMonths: 'week',
    sixMonths: 'month',
    oneYear: 'month'
  };

  const [dataByInterval, setDataByInterval] = useState([]);

  useEffect(() => {
    data && data.length && prepareData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalSelected, data]);

  const getStartDate = end => {
    switch (intervalSelected) {
      case 'oneWeek':
        return new Date(end.setDate(end.getDate() - 6));
      case 'oneMonth':
        return new Date(new Date(end.setMonth(end.getMonth() - 1)).setDate(end.getDate() + 1));
      case 'threeMonths':
        return new Date(new Date(end.setMonth(end.getMonth() - 3)).setDate(end.getDate() + 1));
      case 'sixMonths':
        return new Date(new Date(end.setMonth(end.getMonth() - 5)).setDate(1));
      case 'oneYear':
        return new Date(
          new Date(new Date(end.setYear(end.getYear() - 1)).setMonth(end.getMonth() + 1)).setDate(1)
        );
      default:
        return new Date(end.setDate(end.getDate() - 6));
    }
  };

  const addTimeStep = (date, step) => {
    if (step === 'month') {
      return new Date(date.setMonth(date.getMonth() + 1));
    }
    let days = step === 'day'? 1 : 6;
    return new Date(date.setDate(date.getDate() + days));
  }

  const prepareGroupedDataKeys = (format, start, firstWeek, end, intialLength) => {
    let groupedData = {};
    while(start <= end) {
      let dateKey = moment(new Date(start)).format(format);
      dateKey = format === 'WW' ? `${dateKey - firstWeek + 1} wk` : dateKey;
      groupedData[dateKey] = new Array(intialLength).fill(0);
      start = addTimeStep(start, intervalsStep[intervalSelected]);
    }
    return groupedData;
  }

  const groupDataByInterval = (format, dataRange, start, end) => {
    let firstWeek =
      format === 'WW' ? (moment(new Date(start)).format(format)) : 0;
    let groupedData = prepareGroupedDataKeys(
      format,
      new Date(start),
      firstWeek,
      new Date(end),
      yAxis.length
    );
    (dataRange || []).forEach(arr => {
      let dateKey = moment(new Date(arr[0])).format(format);
      dateKey = format === 'WW' ? `${dateKey - firstWeek + 1} wk` : dateKey;
      groupedData[dateKey] = groupedData[dateKey] || [];
      arr.slice(1).forEach((element, index) => {
        // array for the case of more than one line
        groupedData[dateKey][index] = groupedData[dateKey][index] || 0;
        groupedData[dateKey][index] = groupedData[dateKey][index] + ~~element;
      });
    });
    setDataByInterval(Object.entries(groupedData).map(arr => arr.flat()));
  };

  const prepareData = () => {
    if (data.length) {
      let start = getStartDate(new Date(new Date().setHours(0,0,0,0)));
      let dataRange = data.filter(arr => new Date(arr[0]) >= start);
      groupDataByInterval(
        intervalsFormats[intervalSelected],
        dataRange,
        start,
        new Date(new Date().setHours(23,59,59,999))
      )
    }
  };

  return (
    <div {...props} className="graphs-block">
      <header>
        <h5>{title}</h5>
        {showSwitchBtn && (
          <div className="switch-small">
            <ButtonSwitch
              value={isEnabled}
              handleChange={updateClientTrack}
              trackKey={trackKey}
              clientId={clientId}
            />
          </div>
        )}
      </header>
      {dataByInterval && !!dataByInterval.length ? (
        <>
          <div className="graphs-block-time">
            {intervals.map(interval => (
              <span
                key={shortid.generate()}
                onClick={() => setIntervalSelected(interval.key)}
                className={`${
                  intervalSelected === interval.key ? 'active' : ''
                }`}
              >
                {interval.value}
              </span>
            ))}
          </div>
          <div className="graphs-block-content">
            <Chart
              width={'100%'}
              height={'140px'}
              chartType="AreaChart"
              loader={<div>Loading Chart</div>}
              data={[[xAxis, ...yAxis], ...dataByInterval]}
              options={{
                legend: { position: 'none' },
                hAxis: {
                  gridlines: { count: 7, color: '#2f2f2e' },
                  textStyle: {
                    color: '#f9f9f9',
                    fontSize: 12,
                    fontName: 'Montserrat'
                  },
                  minorGridlines: { count: 0 }
                },
                vAxis: {
                  gridlines: { count: 4, color: '#2f2f2e' },
                  minValue: 0,
                  // maxValue: 2000,
                  minorGridlines: { count: 0 },
                  textStyle: {
                    color: '#f9f9f9',
                    fontSize: 12,
                    fontName: 'Montserrat'
                  },
                  format: '####'
                },
                areaOpacity: 0.2,
                backgroundColor: { fill: '#222222', stroke: '#d73255' },
                colors: ['#d73255'],
                pointSize: 3,
                lineWidth: 1,
                chartArea: {
                  // right: 0,
                  width: '75%',
                  height: '110px'
                }
              }}
            />
          </div>
        </>
      ) : (
        <div className="no-graph-container">
          <h4 className="no-graph-text">No Logged Data!</h4>
        </div>
      )}
    </div>
  );
};

function mapDispatchToProps(dispatch) {
  return {
    updateClientTrack: (clientId, values) =>
      dispatch(updateClientTrack(clientId, values))
  };
}

export default connect(null, mapDispatchToProps)(ProgressGraphsItem);
